import type { Navigation, NavigationPageItem } from '../types/navigation';
import type { TranslateFunction } from '@locus-taxy/encore-i18n-toolkit';
import type {
  IconType,
  NavGroup,
  NavLeaf,
  NavNode,
} from '@locus-taxy/disco-ui';
import {
  Boxes,
  ClipboardList,
  Database,
  FlaskConical,
  Folder,
  LineChart,
  Map,
  Package,
  Route,
  Settings,
  ShoppingBag,
  SlidersHorizontal,
  Truck,
  Users,
} from 'lucide-react';
import { getConfiguredIcon } from './iconRegistry';

/* Title-keyword → icon mapping. First match wins; longer/more-specific
   keywords go first so e.g. "Shipment Plan" picks Truck before falling
   through to a generic. The mapping is intentionally a simple list rather
   than a registry — for the POC we want a sensible default per group, not
   exhaustive coverage of every entity. */
const ICON_RULES: Array<[RegExp, IconType]> = [
  [/shipment|tour|trip|consignment|delivery|dispatch/i, Truck],
  [/order|logistic\s*order|sales/i, ShoppingBag],
  [/insight|analytic|report|dashboard/i, LineChart],
  [/master|catalog|reference/i, Database],
  [/setting|rule|config|admin/i, Settings],
  [/test|qa|debug/i, FlaskConical],
  [/platform|module|app/i, Boxes],
  [/network|map|location|geo/i, Map],
  [/route|path/i, Route],
  [/user|account|profile|team|role/i, Users],
  [/task|job|workflow/i, ClipboardList],
  [/filter|param/i, SlidersHorizontal],
];

function pickIcon(title: string, fallback: IconType): IconType {
  const match = ICON_RULES.find(([pattern]) => pattern.test(title));
  return match ? match[1] : fallback;
}

/* Resolve a node's icon, in priority order:
     1. the icon configured on the node (Frontend Modules configurator, or the
        entity schema's `icon`) when it names a real lucide icon;
     2. the title-keyword rules above;
     3. the caller's fallback (Folder for a group, Package for a leaf).

   Configured icons win because someone chose them deliberately. The keyword
   rules stay as the guess for nodes that carry no icon — which is most of the
   shipped config today. Names that don't resolve (the Encore `L*` set, or a
   typo) fall through to the guess rather than rendering nothing. */
function resolveIcon(
  iconName: string | undefined,
  title: string,
  fallback: IconType
): IconType {
  return getConfiguredIcon(iconName) ?? pickIcon(title, fallback);
}

/* Strip toolpad's `../` backtracking prefix and ensure a leading `/`.
   Toolpad emits leaf `segment` like `../clientId/.../entities/foo` to back
   out of group segments in its internal router; disco-ui hands `href` to
   react-router's <Link>, which wants an absolute path. */
function toAbsoluteHref(segment: string): string {
  const stripped = segment.replace(/^(\.\.\/)+/, '');
  return stripped.startsWith('/') ? stripped : `/${stripped}`;
}

function isPageItem(item: Navigation[number]): item is NavigationPageItem {
  return (item as NavigationPageItem).segment !== undefined;
}

function convert(item: NavigationPageItem, activePath: string): NavNode {
  const title = String(item.title ?? '');
  if (item.children && item.children.length > 0) {
    const items = item.children
      .filter(isPageItem)
      .map(child => convert(child, activePath));
    return {
      kind: 'group',
      title,
      icon: resolveIcon(item.iconName, title, Folder),
      defaultOpen: containsActiveLeaf(items, activePath),
      items,
    } satisfies NavGroup;
  }
  return {
    kind: 'leaf',
    title,
    icon: resolveIcon(item.iconName, title, Package),
    href: toAbsoluteHref(String(item.segment ?? '')),
    // Omitted rather than passed as `undefined`: `SidebarMenuBadge` renders on
    // presence, so an explicit undefined would still be a declared prop.
    ...(item.badge !== undefined && { badge: item.badge }),
  } satisfies NavLeaf;
}

function containsActiveLeaf(items: NavNode[], activePath: string): boolean {
  if (!activePath) {
    return false;
  }
  return items.some(node =>
    node.kind === 'leaf'
      ? node.href === activePath
      : containsActiveLeaf(node.items, activePath)
  );
}

function collectLeafHrefs(nodes: NavNode[], acc: string[] = []): string[] {
  for (const node of nodes) {
    if (node.kind === 'leaf') {
      if (node.href) {
        acc.push(node.href);
      }
    } else {
      collectLeafHrefs(node.items, acc);
    }
  }
  return acc;
}

/* Map an arbitrary route onto the nav leaf that owns it. An entity *detail*
   page like `/clientId/.../entities/customerShipment/cs_123` is not itself a
   nav leaf — its owning leaf is the list route
   `/clientId/.../entities/customerShipment`. AppShell highlights a leaf on
   exact `pathname === href` and we pre-open the group via the same equality,
   so without this normalisation a detail page highlights nothing and its
   parent group collapses ("nav resets"). Longest matching prefix wins; falls
   back to the raw pathname when no leaf is an ancestor. */
export function resolveActiveHref(
  navigation: Navigation,
  pathname: string
): string {
  const hrefs = collectLeafHrefs(toDiscoNav(navigation));
  let best = '';
  for (const href of hrefs) {
    if (
      (pathname === href || pathname.startsWith(`${href}/`)) &&
      href.length > best.length
    ) {
      best = href;
    }
  }
  return best || pathname;
}

/* The icon of the top-level nav group that owns `pathname`.

   encore-grid puts an icon in the ornament ahead of a workflow action's title
   and cannot resolve one itself (it has no nav tree), so the host passes it
   down. The ask is for the ornament to carry the same icon as the current
   page's parent nav group.

   This reads the icon straight off the built `NavNode` tree rather than
   re-running `resolveIcon`, so the ornament cannot drift from what the sidebar
   actually renders — configured `iconName` first, then the title-keyword guess,
   then `Folder`, whatever that chain happens to produce.

   `resolveActiveHref` first, so a detail route (`.../entities/foo/<id>`), which
   is not a nav leaf itself, resolves through its owning list leaf.

   Walks the ORIGINAL top-level items and converts each group, rather than
   iterating `toDiscoNav`'s output. `toDiscoNav` wraps loose top-level leaves in
   a synthetic "Modules" group so AppShell has something to render them under;
   that group is a rendering device, not a group anyone configured, and matching
   it would hand back its `Folder` icon for a route that belongs to no real
   group. `convert` is the same function `toDiscoNav` uses, so reading the icon
   off its output still cannot drift from the sidebar.

   Returns undefined when no configured top-level group owns the route — a loose
   top-level leaf, or a path outside the nav entirely. Callers hand that to
   encore-grid as no icon at all, and it falls back to its own generic box. */
export function resolveActiveGroupIcon(
  navigation: Navigation,
  pathname: string
): IconType | undefined {
  const activeHref = resolveActiveHref(navigation, pathname);
  const owner = navigation
    .filter(isPageItem)
    .filter(item => item.children?.length)
    .map(item => convert(item, ''))
    .find(
      node =>
        node.kind === 'group' && containsActiveLeaf(node.items, activeHref)
    );
  return owner?.kind === 'group' ? owner.icon : undefined;
}

/* Convert a toolpad Navigation to disco-ui NavNode[]. AppShell renders a
   top-level leaf as readily as a top-level group; `wrapOrphans` keeps the
   main sidebar's long-standing synthetic "Modules" grouping, and callers
   that want loose leaves rendered as-is (the bottom nav) pass `false`.
   The `activePath` arg lets the converter pre-open any group whose subtree
   contains the currently-routed leaf, so the user lands with the active
   item already visible (and highlighted). */
export function toDiscoNav(
  navigation: Navigation,
  activePath = '',
  t: TranslateFunction = (_, d) => d,
  wrapOrphans = true
): NavNode[] {
  const nodes = navigation
    .filter(isPageItem)
    .map(item => convert(item, activePath));

  if (!wrapOrphans) {
    return nodes;
  }

  const groups = nodes.filter(
    (node): node is NavGroup => node.kind === 'group'
  );
  const orphanLeaves = nodes.filter(
    (node): node is NavLeaf => node.kind === 'leaf'
  );

  if (orphanLeaves.length > 0) {
    groups.unshift({
      kind: 'group',
      title: t('dashboard.nav.modules', 'Modules'),
      icon: Folder,
      defaultOpen: true,
      items: orphanLeaves,
    });
  }

  return groups;
}
